import { useState, useRef, useEffect } from "react";

// global context for state management
import { useSampleContext } from "../../contexts";
import { useReachbackContext } from "../../contexts/ReachbackContext";

// components
import { SequenceE } from "../ui/Sequence";
import CustomModalT from "../ui/ModalT";

import OverlayTrigger from "react-bootstrap/OverlayTrigger";
import Tooltip from "react-bootstrap/Tooltip";

// queries
import { useGetMeasurementTrace, useValidateSample } from "../../api";

// lodash to help with object comparison
const some = require("lodash.some");

const WasatchWizardTrace = ({ step, changeStep, pageID }) => {
  const [scanResults, setScanResults] = useState([]);
  const [selected, setSelected] = useState({});
  const [counter, setCounter] = useState(1);
  const [error, setError] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [unverifiedScan, setUnverifiedScan] = useState();
  const [showModal, setShowModal] = useState(false);
  // Flags to ensure single processing per user-triggered scan
  const awaitingScanRef = useRef(false);
  const awaitingValidateRef = useRef(false);

  const { initWasatchTrace, device } = useSampleContext();
  const { updateSubmittedErrorsT, addReachbackReason, removeReachbackReason } =
    useReachbackContext();
  const {
    data: traceScan,
    status: traceScanStatus,
    isFetching: isFetchingTraceScan,
    refetch: refetchTraceScan,
  } = useGetMeasurementTrace(
    device?.deviceId,
    "trace-burst",
    connectorAppErrorLogger
  );
  const {
    data: validateResponse,
    status: validateStatus,
    isFetching: isFetchingValidate,
    refetch: refetchValidate,
  } = useValidateSample(unverifiedScan, sampleCheckErrorLogger);

  // Helper function to get the two newest scans
  const getTwoNewestScans = (results) => {
    // Filter out failed scans (those with modelPrediction "Scan Failed")
    const validScans = results.filter(
      (scan) => scan.errorCheck?.modelPrediction !== "Scan Failed"
    );
    return validScans.sort((a, b) => b.scanId - a.scanId).slice(0, 2);
  };

  // Initialize the scan results state with the two newest scans
  const [twoNewestScans, setTwoNewestScans] = useState(() =>
    getTwoNewestScans(scanResults)
  );

  // Update the two newest scans whenever scanResults change
  useEffect(() => {
    setTwoNewestScans(getTwoNewestScans(scanResults));
  }, [scanResults]);

  // Reset selected scan if it no longer exists in the two newest scans
  useEffect(() => {
    if (!doesScanExist(selected.scanId)) {
      setSelected({});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [twoNewestScans]);

  // Check if the scan exists in the two newest scans
  const doesScanExist = (scanId) => {
    return some(twoNewestScans, (scan) => scan.scanId === scanId);
  };

  function connectorAppErrorLogger(err) {
    console.log("Trace scan error logged.");
    setLoading(false);
    setError(true);
    if (err?.message === "Network Error") {
      setErrorMessage(
        "Unable to reach Connector App. Please ensure it is running and try again."
      );
    } else {
      setErrorMessage(
        err?.response?.data?.error?.message ||
          err?.response?.data?.detail ||
          err?.message ||
          "Failed to perform trace scan. Please ensure the device is connected and try again."
      );
    }

    // Add a failed scan entry so previous scans don't disappear
    if (awaitingScanRef.current) {
      const failedScan = {
        scanId: counter,
        verified: false,
        errorCheck: {
          errorFlag: -1,
          modelPrediction: "Scan Failed",
          reconstructionError: null,
          signalQuality: null,
        },
        // Add minimal data structure to prevent rendering errors
        wavelengths: [],
        intensities: [],
      };
      setScanResults((prev) => [...prev, failedScan]);
    }

    // Only reset scan ref - validation hasn't started yet if scan failed
    awaitingScanRef.current = false;
    console.error(err);
  }

  function sampleCheckErrorLogger(err) {
    console.log("Error logged.");
    setLoading(false);
    setError(true);
    setErrorMessage(
      err?.response?.data?.error?.message ||
        err?.response?.data?.detail ||
        err?.message ||
        "Failed to validate scan. Please try again."
    );
    // Reset both refs - scan succeeded but validation failed
    awaitingScanRef.current = false;
    awaitingValidateRef.current = false;
    console.error(err);
  }

  const handleScan = () => {
    console.log("Performing trace scan");
    setLoading(true);
    setError(false);
    setErrorMessage("");
    awaitingScanRef.current = true;
    refetchTraceScan();
    setShowModal(false);
  };

  const handleModal = () => {
    // After completing the scan, show the modal
    setShowModal(true);
  };

  // Once the trace scan is retrieved, prepare it for validation
  useEffect(() => {
    if (
      awaitingScanRef.current &&
      !isFetchingTraceScan &&
      step === pageID &&
      traceScanStatus === "success"
    ) {
      setUnverifiedScan({ ...(traceScan.data || traceScan), scanId: counter });
      setCounter((prev) => prev + 1);
      awaitingValidateRef.current = true;
      awaitingScanRef.current = false;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [traceScan, isFetchingTraceScan, traceScanStatus, step, pageID]);

  // Only attempt to validate scan once react has set the 'unverifiedScan' variable
  useEffect(() => {
    if (unverifiedScan) {
      refetchValidate();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [unverifiedScan]);

  // Check the scan validate query is done
  useEffect(() => {
    if (!isFetchingValidate && step === pageID) {
      if (validateStatus === "success" && awaitingValidateRef.current) {
        let verifiedScan = { ...unverifiedScan };

        verifiedScan.verified =
          validateResponse?.data?.errorCheck?.errorFlag === 0;

        // Always add errorCheck properties
        verifiedScan.errorCheck = {
          // errorFlag: subScan?.errorCheck?.errorFlag,
          // errorMessage: subScan?.errorCheck?.errorMessage,
          // errorType: subScan?.errorCheck?.errorType,
          errorFlag: validateResponse?.data?.errorCheck?.errorFlag,
          modelPrediction: validateResponse?.data?.errorCheck?.modelPrediction,
          reconstructionError:
            validateResponse?.data?.errorCheck?.reconstructionError_intensity,
          signalQuality: validateResponse?.data?.errorCheck?.signalQuality,
        };
        console.log(verifiedScan);
        // else
        // if (!verifiedScan.verified) {
        //   verifiedScan.subScans = validateResponse?.data?.subScans.map(
        //     (subScan) => ({
        //       ...subScan,
        //       errorCheck: {
        //         errorFlag: subScan?.errorCheck?.errorFlag,
        //         errorMessage: subScan?.errorCheck?.errorMessage,
        //         errorType: subScan?.errorCheck?.errorType,
        //         modelPrediction: subScan?.errorCheck?.modelPrediction,
        //         reconstructionError: subScan?.errorCheck?.reconstructionError,
        //         signalQuality: subScan?.errorCheck?.signalQuality,
        //       },
        //     })
        //   );
        // }

        setScanResults((prev) => [...prev, verifiedScan]);
        setLoading(false);
        setError(false);
        // Clear validation flag after successful processing
        awaitingValidateRef.current = false;
      } else if (validateStatus === "error" && awaitingValidateRef.current) {
        console.error("Could not validate scan.");
        // Still add the scan to results but mark it as unverified
        if (unverifiedScan) {
          let failedScan = {
            ...unverifiedScan,
            verified: false,
            errorCheck: {
              errorFlag: -1,
              modelPrediction: "Validation Failed",
              reconstructionError: null,
              signalQuality: null,
            },
          };
          setScanResults((prev) => [...prev, failedScan]);
        }
        // Clear validation flag - scan succeeded but validation failed
        awaitingValidateRef.current = false;
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [validateResponse, isFetchingValidate]);

  if (step !== pageID) {
    return null;
  }

  const handleSelectSequence = (sequence) => {
    setSelected(sequence);
    console.log("Selected sequence: ", sequence);
  };

  return (
    // <CardA
    //   title='Amplifi ID Trace Scan'
    //   subtitle='If trace scan is not applicable, press "Skip Trace Scan".'
    //   steps={steps}
    //   step={step - 1}
    // >
    <div className='shadow card  d-flex flex-column flex-grow-1 '>
      <div className='card-body full-height justify-content-between'>
        <div
          className={
            twoNewestScans.length !== 0
              ? "d-flex justify-content-between flex-wrap"
              : "d-flex flex-column justify-content-between flex-grow-1 "
          }
        >
          <div
            className={
              twoNewestScans.length !== 0
                ? "col-12 col-md-6 order-1"
                : "row order-1"
            }
          >
            <h5 className='card-title w-100'>
              <span>Amplifi ID Trace Scan</span>
              <button
                className='btn ms-1 p-1'
                aria-label='Info'
                onClick={handleModal}
              >
                <i
                  className='bi bi-info-circle-fill'
                  style={{ color: "#1ca3f0" }}
                ></i>
              </button>
            </h5>
            <h6 className='card-subtitle mb-2 mb-md-4 text-muted w-100'>
              If Trace Scan is not applicable, press "Skip Trace Scan".
            </h6>
          </div>

          <div
            className={
              twoNewestScans.length !== 0
                ? "col-12 col-md-6 d-flex justify-content-start justify-content-md-end align-items-center order-2"
                : "row pt-4 d-flex flex-column flex-grow-1 justify-content-center align-items-center order-3"
            }
          >
            {!loading && (
              <button
                className='btn btn-link float-end order-1'
                type='button'
                onClick={() => {
                  initWasatchTrace(null);
                  changeStep("forward");
                  updateSubmittedErrorsT(false);
                }}
              >
                Skip Trace Scan
              </button>
            )}
            <button
              className='btn btn-success float-end w-50'
              type='button'
              variant='outline-success'
              onClick={() => handleScan()}
              disabled={loading}
            >
              {loading ? (
                <span>
                  <span
                    className='spinner-border spinner-border-sm'
                    aria-hidden='true'
                  ></span>
                  <span> Scanning...</span>
                </span>
              ) : (
                <span>Conduct Trace Scan</span>
              )}
            </button>
            <CustomModalT
              showModal={showModal}
              handleClose={() => setShowModal(false)}
            />
          </div>

          {/* Error Alert */}
          {error && errorMessage && (
            <div
              className={`col-12 mt-3 ${
                twoNewestScans.length === 0 ? "order-2" : "order-3"
              }`}
            >
              <div
                className='alert alert-danger alert-dismissible fade show'
                role='alert'
              >
                <i className='bi bi-exclamation-triangle-fill me-2'></i>
                <strong>Error:</strong> {errorMessage}
                <button
                  type='button'
                  className='btn-close'
                  data-bs-dismiss='alert'
                  aria-label='Close'
                  onClick={() => {
                    setError(false);
                    setErrorMessage("");
                  }}
                ></button>
              </div>
            </div>
          )}
        </div>
        {twoNewestScans.length !== 0 && (
          <form className='row justify-content-evenly d-flex align-items-stretch flex-wrap flex-grow-1'>
            {twoNewestScans.map((seq) => {
              return (
                <div
                  className='col-12 col-xl-6 mt-2 spectrum-card-container '
                  key={seq.scanId}
                >
                  <SequenceE
                    sequence={seq}
                    isSelected={selected.scanId === seq.scanId}
                    valid={seq.verified}
                    onSelect={handleSelectSequence}
                  />
                </div>
              );
            })}
          </form>
        )}

        <div className='row mt-4 align-content-center'>
          <span className='col-6 d-flex justify-content-center'>
            <button
              type='button'
              className='btn btn-secondary mx-auto w-75'
              onClick={() => changeStep("back")}
              disabled={loading}
            >
              Back
            </button>
          </span>
          <OverlayTrigger
            overlay={
              Object.keys(selected).length === 0 ? (
                <Tooltip id='tooltip-disabled'>
                  Please conduct and select a scan to continue. Otherwise, press
                  "Skip Trace Scan".
                </Tooltip>
              ) : (
                <></>
              )
            }
          >
            <span className='col-6 d-flex justify-content-center'>
              <button
                type='button'
                className='btn btn-primary mx-auto w-75'
                onClick={() => {
                  // if (!submittedErrorsT) {
                  // Check if submittedErrorsT is false

                  const hasError =
                    selected.errorCheck?.modelPrediction !== "Analyte";
                  console.log("hasError? : ", hasError);
                  if (hasError) {
                    updateSubmittedErrorsT(true); // Set submittedErrorsT to true
                    addReachbackReason({
                      traceErrorCheck: selected.errorCheck?.signalQuality,
                    });
                  } else {
                    updateSubmittedErrorsT(false); // Set submittedErrorsT to false
                    removeReachbackReason("traceErrorCheck");
                  }
                  // }
                  initWasatchTrace(selected);
                  changeStep("forward");
                }}
                disabled={Object.keys(selected).length === 0 || loading}
              >
                {selected.errorCheck?.modelPrediction === "Analyte" ||
                Object.keys(selected).length === 0
                  ? "Continue"
                  : "Continue and Submit for Reachback Service"}
              </button>
            </span>
          </OverlayTrigger>
        </div>
      </div>
    </div>
  );
};

export default WasatchWizardTrace;
